/**
 * The metronome's click sounds: each one a short recipe of synthesized layers, rendered once into a
 * buffer and played back on every beat.
 *
 * Plain Web Audio rather than Tone, so the metronome never waits on Tone's download to make its first
 * click. The recipes borrow drumKit.js's ideas — a pitch-dropping tone for a struck body, filtered
 * noise for the strike itself, two squares through a bandpass for a cowbell.
 */

const RENDER_SAMPLE_RATE = 48000;
const DEFAULT_ATTACK = 0.001;
const MAX_LENGTH = 0.6;
const SILENCE = 0.0001;

export const VARIANTS = ['downbeat', 'regular', 'subdivision'];

// How loud each variant peaks against the downbeat
const VARIANT_LEVELS = { downbeat: 1, regular: 0.85, subdivision: 0.45 };

// A subdivision click rings shorter than its beat, so the clicks between beats stay apart
const SUBDIVISION_DECAY = 0.6;

const envelope = (ctx, destination, time, { gain = 1, attack = DEFAULT_ATTACK, decay }) => {
  const node = ctx.createGain();

  node.gain.setValueAtTime(0, time);
  node.gain.linearRampToValueAtTime(gain, time + attack);
  node.gain.exponentialRampToValueAtTime(SILENCE, time + attack + decay);
  node.connect(destination);

  return node;
};

const withFilter = (ctx, destination, filter) => {
  if (!filter) return destination;

  const node = ctx.createBiquadFilter();

  node.type = filter.type;
  node.frequency.value = filter.freq;
  node.Q.value = filter.q ?? 1;
  node.connect(destination);

  return node;
};

const layerEnd = (time, layer) => time + (layer.attack ?? DEFAULT_ATTACK) + layer.decay + 0.01;

const noiseBuffers = new WeakMap();

const getNoiseBuffer = (ctx) => {
  if (!noiseBuffers.has(ctx)) {
    const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * MAX_LENGTH), ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    noiseBuffers.set(ctx, buffer);
  }

  return noiseBuffers.get(ctx);
};

const LAYERS = {
  // A struck body: an oscillator, optionally falling in pitch as it dies away
  tone: (ctx, destination, time, layer) => {
    const oscillator = ctx.createOscillator();

    oscillator.type = layer.wave || 'sine';
    oscillator.frequency.setValueAtTime(layer.freq, time);
    if (layer.freqEnd) oscillator.frequency.exponentialRampToValueAtTime(layer.freqEnd, time + layer.sweep);

    oscillator.connect(envelope(ctx, withFilter(ctx, destination, layer.filter), time, layer));
    oscillator.start(time);
    oscillator.stop(layerEnd(time, layer));
  },

  // The strike itself: a burst of filtered noise
  noise: (ctx, destination, time, layer) => {
    const source = ctx.createBufferSource();

    source.buffer = getNoiseBuffer(ctx);
    source.connect(envelope(ctx, withFilter(ctx, destination, layer.filter), time, layer));
    source.start(time);
    source.stop(layerEnd(time, layer));
  },

  // A bell: one sine bending another's pitch, the bend fading faster than the tone
  fm: (ctx, destination, time, layer) => {
    const carrier = ctx.createOscillator();
    const modulator = ctx.createOscillator();
    const depth = ctx.createGain();
    const modFreq = layer.freq * layer.ratio;
    const deviation = layer.index * modFreq;

    carrier.frequency.setValueAtTime(layer.freq, time);
    modulator.frequency.setValueAtTime(modFreq, time);
    depth.gain.setValueAtTime(deviation, time);
    depth.gain.exponentialRampToValueAtTime(deviation * 0.01, time + layer.decay / 2);

    modulator.connect(depth);
    depth.connect(carrier.frequency);
    carrier.connect(envelope(ctx, withFilter(ctx, destination, layer.filter), time, layer));

    const end = layerEnd(time, layer);

    [carrier, modulator].forEach((oscillator) => {
      oscillator.start(time);
      oscillator.stop(end);
    });
  },
};

export const LAYER_TYPES = Object.keys(LAYERS);

const tone = (freq, decay, gain, options = {}) => ({ type: 'tone', freq, decay, gain, ...options });
const noise = (filter, decay, gain, options = {}) => ({ type: 'noise', filter, decay, gain, ...options });
const fm = (freq, ratio, index, decay, gain) => ({ type: 'fm', freq, ratio, index, decay, gain });
const bandpass = (freq, q = 1) => ({ type: 'bandpass', freq, q });
const highpass = (freq, q = 0.7) => ({ type: 'highpass', freq, q });
const lowpass = (freq, q = 0.7) => ({ type: 'lowpass', freq, q });

// A wooden bar's first two modes sit 2.76 apart
const woodblock = (freq) => [
  tone(freq, 0.07, 1, { freqEnd: freq * 0.97, sweep: 0.02 }),
  tone(freq * 2.76, 0.025, 0.35),
  noise(bandpass(freq * 3, 1.5), 0.008, 0.3),
];

const sideStick = ({ crack, ping, body }) => [
  noise(bandpass(crack, 2), 0.03, 0.8),
  tone(ping, 0.03, 0.6, { wave: 'triangle', freqEnd: ping * 0.67, sweep: 0.015 }),
  tone(body, 0.05, 0.35, { freqEnd: body * 0.6, sweep: 0.02 }),
];

const cowbell = (freq, decay) => [
  tone(freq, decay, 0.5, { wave: 'square', filter: bandpass(freq * 1.48, 1.5) }),
  tone(freq * 1.48, decay, 0.5, { wave: 'square', filter: bandpass(freq * 1.48, 1.5) }),
];

// The six square waves of an 808 hat
const HAT_PARTIALS = [205.3, 304.4, 369.6, 522.7, 540, 800];

const hiHat = (decay, cutoff) => [
  ...HAT_PARTIALS.map((freq) => tone(freq, decay, 0.12, { wave: 'square', filter: highpass(cutoff) })),
  noise(highpass(cutoff), decay, 0.6),
];

const MECHANICAL_TICK = [
  noise(highpass(3000), 0.006, 0.7),
  noise(bandpass(2200, 25), 0.05, 2.5),
  tone(4400, 0.012, 0.15),
];

const softTap = (freq, cutoff) => [
  noise(lowpass(cutoff), 0.05, 0.6, { attack: 0.004 }),
  tone(freq, 0.08, 0.8, { attack: 0.003, freqEnd: freq * 0.8, sweep: 0.03 }),
];

/**
 * The sounds the picker offers, in its order. `level` trims a sound whose ring makes it read louder
 * than its peak says. A sound without its own `subdivision` layers plays its regular beat, shortened.
 */
export const CLICK_SOUNDS = [
  {
    id: 'sideStick',
    label: 'Side Stick',
    downbeat: sideStick({ crack: 4200, ping: 2100, body: 480 }),
    regular: sideStick({ crack: 3500, ping: 1800, body: 400 }),
  },
  { id: 'woodblock', label: 'Woodblock', downbeat: woodblock(1333), regular: woodblock(1000) },
  {
    id: 'clave',
    label: 'Clave',
    downbeat: [tone(3000, 0.06, 1), noise(highpass(5000), 0.004, 0.25)],
    regular: [tone(2500, 0.045, 1), noise(highpass(5000), 0.004, 0.25)],
  },
  { id: 'cowbell', label: 'Cowbell', level: 0.8, downbeat: cowbell(605, 0.3), regular: cowbell(540, 0.1) },
  { id: 'hiHat', label: 'Hi-Hat', downbeat: hiHat(0.16, 6000), regular: hiHat(0.04, 7000) },
  {
    id: 'beep',
    label: 'Beep',
    downbeat: [tone(1500, 0.06, 1, { attack: 0.002 })],
    regular: [tone(1000, 0.06, 1, { attack: 0.002 })],
  },
  {
    id: 'mechanical',
    label: 'Mechanical',
    downbeat: [...MECHANICAL_TICK, fm(2093, 2.76, 1.5, 0.35, 0.5)],
    regular: MECHANICAL_TICK,
  },
  { id: 'bell', label: 'Bell', level: 0.7, downbeat: [fm(1760, 3.5, 2, 0.4, 1)], regular: [fm(880, 3.5, 2, 0.25, 1)] },
  {
    id: 'kickStick',
    label: 'Kick & Stick',
    downbeat: [
      tone(150, 0.25, 1, { freqEnd: 50, sweep: 0.05 }),
      // What a phone speaker, which can't reproduce the kick itself, actually plays
      tone(300, 0.06, 0.4, { freqEnd: 120, sweep: 0.03 }),
      noise(bandpass(2500), 0.006, 0.5),
      tone(1800, 0.012, 0.25, { wave: 'triangle' }),
    ],
    regular: sideStick({ crack: 3500, ping: 1800, body: 400 }),
    subdivision: hiHat(0.03, 8000),
  },
  { id: 'softTap', label: 'Soft Tap', level: 0.6, downbeat: softTap(440, 1600), regular: softTap(330, 1200) },
];

export const getClickSound = (id) => CLICK_SOUNDS.find((sound) => sound.id === id) || null;

export const getLayers = (sound, variant) => {
  if (variant === 'downbeat') return sound.downbeat;

  if (variant === 'subdivision') {
    return sound.subdivision || sound.regular.map((layer) => ({ ...layer, decay: layer.decay * SUBDIVISION_DECAY }));
  }

  return sound.regular;
};

export const variantLevel = (sound, variant) => (sound.level ?? 1) * VARIANT_LEVELS[variant];

/** Play a list of layers into any context — a real one, or the offline one a click is rendered in. */
export const playLayers = (ctx, destination, time, layers) => {
  layers.forEach((layer) => LAYERS[layer.type](ctx, destination, time, layer));
};

const getOfflineContext = () =>
  typeof window === 'undefined' ? null : window.OfflineAudioContext || window.webkitOfflineAudioContext || null;

const startRendering = (ctx) =>
  new Promise((resolve, reject) => {
    // Older Safari only reports the result through oncomplete
    ctx.oncomplete = (event) => resolve(event.renderedBuffer);

    const rendering = ctx.startRendering();

    if (rendering?.then) rendering.then(resolve, reject);
  });

/** Render one variant of a sound, peak-normalised to its level, as raw samples. */
export const renderClick = async (sound, variant) => {
  const OfflineContext = getOfflineContext();
  const layers = getLayers(sound, variant);
  const length = Math.min(MAX_LENGTH, Math.max(...layers.map((layer) => layerEnd(0, layer))));
  const ctx = new OfflineContext(1, Math.ceil(length * RENDER_SAMPLE_RATE), RENDER_SAMPLE_RATE);

  playLayers(ctx, ctx.destination, 0, layers);

  const data = new Float32Array((await startRendering(ctx)).getChannelData(0));
  const peak = data.reduce((max, sample) => Math.max(max, Math.abs(sample)), 0);
  const scale = peak > 0 ? variantLevel(sound, variant) / peak : 0;

  for (let i = 0; i < data.length; i++) data[i] *= scale;

  return data;
};

// Rendered samples, or the render still in flight, by sound and variant
const rendered = new Map();

const variantKey = (id, variant) => `${id}:${variant}`;

/** Render every variant of a sound ahead of its first beat. Safe to call repeatedly. */
export const prepareClickSound = (id) => {
  const sound = getClickSound(id);

  if (!sound || !getOfflineContext()) return Promise.resolve();

  return Promise.all(
    VARIANTS.map((variant) => {
      const key = variantKey(id, variant);

      if (!rendered.has(key)) {
        rendered.set(
          key,
          renderClick(sound, variant).then(
            (data) => {
              rendered.set(key, data);

              return data;
            },
            (error) => {
              rendered.delete(key);
              console.warn(`Metronome sound ${key} could not be rendered`, error);
            },
          ),
        );
      }

      return rendered.get(key);
    }),
  );
};

const contextBuffers = new WeakMap();

/**
 * The rendered click as a buffer of the context about to play it, or null if it isn't rendered yet.
 * Each context gets its own copy, so a natively rendered buffer never crosses into Tone's context.
 */
export const getClickBuffer = (ctx, id, variant) => {
  const key = variantKey(id, variant);
  const data = rendered.get(key);

  if (!(data instanceof Float32Array)) return null;

  if (!contextBuffers.has(ctx)) contextBuffers.set(ctx, new Map());

  const buffers = contextBuffers.get(ctx);

  if (!buffers.has(key)) {
    const buffer = ctx.createBuffer(1, data.length, RENDER_SAMPLE_RATE);

    buffer.getChannelData(0).set(data);
    buffers.set(key, buffer);
  }

  return buffers.get(key);
};
